import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@mainstage/core-plugin-api';
import { ResponseError } from '@mainstage/errors';
import { z } from 'zod';

export const wittySkillZod = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  content: z.string(),
  enabled: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type WittySkill = z.infer<typeof wittySkillZod>;

export const wittySkillUpdateZod = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().optional(),
  content: z.string().min(1).optional(),
  enabled: z.boolean().optional(),
});

export type WittySkillUpdate = z.infer<typeof wittySkillUpdateZod>;

export const wittySkillCreateZod = z.object({
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  content: z.string().min(1),
  enabled: z.boolean().optional(),
});

export type WittySkillCreate = z.infer<typeof wittySkillCreateZod>;

export type WittySkillsApi = {
  listSkills(): Promise<WittySkill[]>;
  getSkill(id: string): Promise<WittySkill>;
  updateSkill(id: string, update: WittySkillUpdate): Promise<WittySkill>;
  createSkill(data: WittySkillCreate): Promise<WittySkill>;
  deleteSkill(id: string): Promise<void>;
};

export const wittySkillsApiRef: ApiRef<WittySkillsApi> =
  createApiRef<WittySkillsApi>({
    id: 'plugin.witty-skills',
  });

export class WittySkillsClient implements WittySkillsApi {
  constructor(
    private readonly fetchApi: FetchApi,
    private readonly discoveryApi: DiscoveryApi,
  ) {}

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('witty');
  }

  async listSkills(): Promise<WittySkill[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/v1/skills`);
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const data: unknown = await response.json();
    const parsed = z.array(wittySkillZod).safeParse(data);
    if (!parsed.success) {
      throw new Error(`Invalid skills response: ${parsed.error.message}`);
    }
    return parsed.data;
  }

  async getSkill(id: string): Promise<WittySkill> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/skills/${encodeURIComponent(id)}`,
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const data: unknown = await response.json();
    const parsed = wittySkillZod.safeParse(data);
    if (!parsed.success) {
      throw new Error(`Invalid skill response: ${parsed.error.message}`);
    }
    return parsed.data;
  }

  async updateSkill(id: string, update: WittySkillUpdate): Promise<WittySkill> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/skills/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(update),
      },
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const data: unknown = await response.json();
    const parsed = wittySkillZod.safeParse(data);
    if (!parsed.success) {
      throw new Error(`Invalid skill response: ${parsed.error.message}`);
    }
    return parsed.data;
  }

  async createSkill(skill: WittySkillCreate): Promise<WittySkill> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/v1/skills`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(skill),
    });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const data: unknown = await response.json();
    const parsed = wittySkillZod.safeParse(data);
    if (!parsed.success) {
      throw new Error(`Invalid skill response: ${parsed.error.message}`);
    }
    return parsed.data;
  }

  async deleteSkill(id: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/skills/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }
}
